/**
 * SignOutConfirm (Story #5351 W1-T17, Screen Contract G11: "Sign out").
 * Entry point per contract: "Account menu → Sign out". Replaces Header's
 * previous direct `onClick={logout}` — signing out is now a separate
 * deliberate confirm step, matching the contract's "confirmation-and-
 * outcome" screen type.
 *
 * Honest scope (verified against packages/shared-crm/src/auth/logout.ts,
 * the ONLY sign-out implementation in this codebase): performLogout()
 * clears localStorage keys, the React Query cache and navigates to
 * /login. There is NO provider-global (Cognito) sign-out call anywhere —
 * no `global: true` revocation, no server round trip. So this dialog
 * offers exactly one real action ("Sign out", local) and does not offer a
 * second "sign out of all sessions" button that would silently do the same
 * thing under a false label — that would be exactly the kind of
 * fabricated capability DoD-07 exists to prevent. The gap is disclosed via
 * UnavailableReason instead, matching the honest-gap precedent set by
 * ApprovalsPanel's "approval backend pending" text.
 *
 * Unsaved-changes warning uses the existing dirty-form guard (W1 S2b,
 * utils/dirtyGuard.ts) — the same mechanism ScopeRibbon's environment
 * switch already respects, so a dirty page is protected consistently by
 * every shell control that navigates away from it, not just the ribbon.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isDirty } from '../../utils/dirtyGuard';
import UnavailableReason from '../ui/UnavailableReason';

interface SignOutConfirmProps {
  onCancel: () => void;
}

const SignOutConfirm: React.FC<SignOutConfirmProps> = ({ onCancel }) => {
  const { user, logout } = useAuth();
  const dirty = isDirty();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Sign out"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5">
        <h2 className="text-base font-semibold text-gray-900">Sign out</h2>
        <p className="text-sm text-gray-500 mt-1">
          Signed in as {user?.email || user?.username || 'this account'}.
        </p>

        {dirty && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>You have unsaved changes on the current page. Signing out discards them.</span>
          </div>
        )}

        <UnavailableReason
          className="mt-3"
          reason="This clears your local session only (tokens, cache and storage on this browser). Provider-global session revocation is not implemented yet."
        />

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={logout}
            className="flex-1 px-3 py-2 rounded-lg bg-red-600 text-sm text-white hover:bg-red-700"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignOutConfirm;
